import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { updateAdminPassword } from '../../services/api';

const EyeIcon = ({ open }) => open ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
) : (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
);

export default function ForcePasswordChange() {
  const { token, auth, login, logout } = useApp();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent]         = useState(false);
  const [showNew, setShowNew]                 = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword === 'TransferExpo2026!') { setError('Please choose a different password from the temporary one'); return; }

    setLoading(true);
    try {
      await updateAdminPassword(token, currentPassword, newPassword);
      // Clear mustChangePassword from session and re-login
      const result = await login({ email: auth.email, password: newPassword });
      if (!result.ok) {
        setError('Password changed but auto-login failed. Please log in again.');
        setTimeout(() => logout(), 2000);
      }
    } catch (e) {
      setError(e.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    flex: 1, background: 'var(--bg-input)', border: 'none', outline: 'none',
    color: 'var(--text-primary)', fontSize: 13, padding: '10px 12px',
  };
  const wrapStyle = {
    display: 'flex', alignItems: 'center',
    background: 'var(--bg-input)', border: '1px solid var(--border)',
    borderRadius: 6, overflow: 'hidden', marginBottom: 0,
  };
  const toggleBtn = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-dim)', padding: '0 12px', display: 'flex', alignItems: 'center',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-page)', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 8 }}>
            Set your password
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Welcome to TransferExpo CRM! For security, you must set a new password before continuing.
          </p>
        </div>

        <div className="card" style={{ padding: 28 }}>
          <div style={{ background: 'rgba(167,244,50,0.08)', border: '1px solid rgba(167,244,50,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: 'var(--text-muted)' }}>
            Logged in as <strong style={{ color: 'var(--accent)' }}>{auth?.email}</strong>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>TEMPORARY PASSWORD</label>
              <div style={wrapStyle}>
                <input type={showCurrent ? 'text' : 'password'} value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="Enter your temporary password" style={inputStyle} required />
                <button type="button" style={toggleBtn} onClick={() => setShowCurrent(p => !p)}>
                  <EyeIcon open={showCurrent} />
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>NEW PASSWORD</label>
              <div style={wrapStyle}>
                <input type={showNew ? 'text' : 'password'} value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters" style={inputStyle} required />
                <button type="button" style={toggleBtn} onClick={() => setShowNew(p => !p)}>
                  <EyeIcon open={showNew} />
                </button>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>CONFIRM NEW PASSWORD</label>
              <div style={wrapStyle}>
                <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password" style={inputStyle} required />
                <button type="button" style={toggleBtn} onClick={() => setShowConfirm(p => !p)}>
                  <EyeIcon open={showConfirm} />
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.3)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#ff4d4d' }}>
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', fontSize: 14 }} disabled={loading}>
              {loading ? 'Updating password…' : 'Set new password & continue'}
            </button>
          </form>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--text-dim)' }} onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
